import LinearProgress, { LinearProgressProps } from '@mui/material/LinearProgress'
import Typography, { TypographyOwnProps } from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { Paper } from '../../common/Form'
import { useState } from 'react'
import { styled } from '@mui/material/styles'

export type CommandStatus =
  | CommandStatusNotStarted
  | CommandStatusInProgress
  | CommandStatusFailed
  | CommandStatusSuccess

interface CommandStatusBase<T> {
  id: string
  title: string
  createdAt: Date
  type: T
}

interface CommandStatusNotStarted extends CommandStatusBase<'notstarted'> {}

interface CommandStatusInProgress extends CommandStatusBase<'inprogress'> {
  progressPercentage: number
  message: string
}

interface CommandStatusFailed extends CommandStatusBase<'failed'> {
  message: string
}

interface CommandStatusSuccess extends CommandStatusBase<'success'> {}

type NoType<T> = Omit<T, 'type'>

export const commandNotStarted = (x: NoType<CommandStatusNotStarted>): CommandStatusNotStarted => ({
  ...x,
  type: 'notstarted',
})
export const commandInProgress = (x: NoType<CommandStatusInProgress>): CommandStatusInProgress => ({
  ...x,
  type: 'inprogress',
})
export const commandFailed = (x: NoType<CommandStatusFailed>): CommandStatusFailed => ({ ...x, type: 'failed' })
export const commandSuccess = (x: NoType<CommandStatusSuccess>): CommandStatusSuccess => ({ ...x, type: 'success' })

export interface CommandListProps {
  statuses: CommandStatus[]
}

export const CommandList = ({ statuses }: CommandListProps) => {
  return (
    <Paper title="Jobs">
      {statuses.map((status) => (
        <CommandStatusItem key={status.id} {...status} />
      ))}
    </Paper>
  )
}

const CommandStatusItem = (status: CommandStatus) => {
  let v: { statusText?: string } & Required<Pick<LinearProgressProps, 'color' | 'value'>>
  switch (status.type) {
    case 'notstarted': {
      v = {
        value: 0,
        color: 'secondary',
        statusText: 'Queued',
      }
      break
    }
    case 'inprogress': {
      v = {
        value: status.progressPercentage,
        color: 'secondary',
        statusText: status.message,
      }
      break
    }
    case 'failed': {
      v = {
        value: 100,
        color: 'error',
        statusText: status.message,
      }
      break
    }
    case 'success': {
      v = {
        value: 100,
        color: 'success',
      }
      break
    }
    default: {
      const _exhaustiveCheck: never = status
      return _exhaustiveCheck
    }
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  return (
    <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column', marginTop: 3, marginLeft: 2 }}>
      <Typography variant="h5">{status.title}</Typography>
      <LinearProgressWithLabel variant="determinate" color={v.color} value={v.value} />
      {v.statusText && <StatusText color="textSecondary">{v.statusText}</StatusText>}
      <Typography variant="caption" color="textSecondary  " sx={{ fontStyle: 'italic', alignSelf: 'end' }}>
        Created at {formatter.format(status.createdAt)}
      </Typography>
    </Box>
  )
}

const StatusText = ({ color, children }: { children: string } & Pick<TypographyOwnProps, 'color'>) => {
  const [expanded, setExpanded] = useState(false)
  const maxLength = 100
  const isLong = children.length > maxLength

  const toggleExpanded = () => setExpanded((prev) => !prev)

  const getMessage = () => {
    if (isLong && !expanded) {
      return children.slice(0, maxLength) + '…'
    } else {
      return children
    }
  }
  return (
    <Box>
      <Typography variant="body2" color={color}>
        {getMessage()}
        {isLong && <ShowMore onClick={toggleExpanded}>{expanded ? 'Show less' : 'Show more'}</ShowMore>}
      </Typography>
    </Box>
  )
}

const ShowMore = styled('span')(({ theme }) => ({
  color: theme.palette.secondary.main,
  cursor: 'pointer',
  marginLeft: theme.spacing(0.5),
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1.5,
  fontStyle: 'italic',
  '&:hover': {
    textDecoration: 'underline',
  },
  '&:focus': {
    outline: `2px solid ${theme.palette.secondary.main}`,
    outlineOffset: '2px',
  },
}))

type LinearProgressWithLabelProps = LinearProgressProps & { value: number }

const LinearProgressWithLabel = (props: LinearProgressWithLabelProps) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Box sx={{ width: '100%' }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 10, marginLeft: 1 }}>
        <Typography variant="body2" color="textPrimary">{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  )
}
